/**
 * Loads a JSON-serialized object from localStorage.
 *
 * @param key - The localStorage key where the object is stored.
 * @returns The deserialized object, or `null` if the key is not found.
 */
export function loadObjectFromLocalStorage<T>(key: string): T | null {
	const data = localStorage.getItem(key)
	return data ? JSON.parse(data) : null
}

/**
 * Serializes an object and saves it to localStorage.
 *
 * @param key - The localStorage key under which to store the object.
 * @param value - The object to serialize and store.
 */
export function saveObjectToLocalStorage<T>(key: string, value: T): void {
	localStorage.setItem(key, JSON.stringify(value))
}

/**
 * Removes a specific property from a JSON-serialized object in localStorage.
 *
 * @param storageKey - The key under which the object is stored in localStorage.
 * @param propertyKey - The property to remove from the stored object.
 *
 * If the object or property does not exist, this function does nothing.
 */
export function removePropertyFromStoredObject(
	storageKey: string,
	propertyKey: string
): void {
	// Load existing data from localStorage
	const storedObject =
		loadObjectFromLocalStorage<Record<string, unknown>>(storageKey)

	// If either the object or the specified property is missing, do nothing
	if (
		!storedObject ||
		!Object.prototype.hasOwnProperty.call(storedObject, propertyKey)
	) {
		return
	}

	// Remove the specified property
	delete storedObject[propertyKey]

	// If there are not more items to save, delete the object from the LocalStorage
	if (Object.keys(storedObject).length === 0) {
		localStorage.removeItem(storageKey)
	} else {
		// Save the updated object back to localStorage
		saveObjectToLocalStorage(storageKey, storedObject)
	}
}
